import { CloseIcon } from "@chakra-ui/icons";
import { Button } from "@chakra-ui/react";
import api from "api/api";
import { CustomModal } from "components/custom-modal";
import DynamicTable from "components/dynamic-table";
import { Column } from "components/dynamic-table/types";
import { ChangeIcon } from "components/vectors/change-icon";
import { useState } from "react";
import { useEventListener, makeEvent } from "services/events";
import { Attendance } from "../atendimento-form/types";
import { Toast } from "components/toast";
import { AttendanceChangeBodyParams } from "../kanban-and-table-atendimento/kanban/parts/types";
import { useAttendanceKanbanContext } from "../kanban-and-table-atendimento/kanban/kanban-context";
import { useChatsContext } from "../whatsapp-attendances/components/chats-context";

export interface UsersProps {
  id: number;
  username: string;
  name: string;
}

interface AttendanceId {
  id: Attendance["id"];
  ids?: number[];
}

export function ModalChangeUser() {
  const [users, setUsers] = useState<UsersProps[]>([]);
  const [changeUserModalData, setChangeUserModalData] =
    useState<AttendanceId | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { visualizationMode, setKanbanColumns, kanbanColumns, handleDrop } =
    useAttendanceKanbanContext();
  const { setAttendanceMap } = useChatsContext();

  const onUpdate = ({
    id,
    etapa,
    tabulacao,
    userName,
  }: AttendanceChangeBodyParams) => {
    const attendanceId = id;
    const newUserName = userName;
    if (visualizationMode === "kanban") {
      setKanbanColumns((columns) =>
        columns.map((column) => {
          const searchId = column.attendancesList.find(
            (curr) => curr.id === attendanceId
          );
          if (searchId) {
            column.attendancesList = column.attendancesList.map((attendace) => {
              if (attendace.id === attendanceId) {
                attendace.userName = newUserName;
                attendace.tabulacao = tabulacao;
              }
              return attendace;
            });
          }
          return column;
        })
      );
      let index: number = 0;
      let col = kanbanColumns.find(({ attendancesList }) => {
        const matched = attendancesList.find(({ id }, i) => {
          if (attendanceId === id) {
            index = i;
            return true;
          } else return false;
        });
        return !!matched;
      });

      handleDrop({
        source: { droppableId: col?.colId!, index },
        destination: { droppableId: etapa, index: 0 },
        persistMovement: false,
      });
    } else if (visualizationMode === "whats-app-attendances") {
      setAttendanceMap((prevMap) => {
        let data = prevMap.get(attendanceId);
        if (data) {
          data.etapa = etapa;
          data.usuarioNome = userName;
          data.tabulacao = tabulacao;
          data.dataAtualizacao = new Date().getTime();
        }
        prevMap.set(attendanceId, data!);
        return new Map(prevMap.entries());
      });
    }
  };

  async function handleChangeUser({
    toUserId,
    attendanceId,
  }: {
    toUserId: number;
    attendanceId: number;
  }) {
    setIsLoading(true);
    try {
      const { data } = await api.get<AttendanceChangeBodyParams>(
        `atendimentos/${attendanceId}/transfer/${toUserId}`
      );
      Toast({
        title: "Atendimento transferido com sucesso!",
        status: "success",
      });
      onClose();
      onUpdate(data);
    } catch (e) {
      Toast({ title: "Erro ao transferir atendimento!", status: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get("/usuarios/possible-transfer-attendance");
      setUsers(data);
    } catch (e) {
      Toast({ title: "Erro ao carregar usuários!", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setChangeUserModalData(null);
    setUsers([]);
    setIsLoading(false);
  };

  const onOpen = (data: AttendanceId) => {
    setIsOpen(true);
    getUsers();
    setChangeUserModalData(data);
  };

  useEventListener("openModalUserTransferAttendance", onOpen);

  const columns: Column[] = [
    {
      name: "Nome do Usuário",
      key: "name",
    },
    {
      name: "Ações",
      render: (row) => (
        <Button
          onClick={() =>
            handleChangeUser({
              attendanceId: changeUserModalData?.id!,
              toUserId: row.id,
            })
          }
          leftIcon={<ChangeIcon />}
          size="sm"
          variant="outline"
        >
          Transferir
        </Button>
      ),
    },
  ];

  const footer = (
    <>
      <Button
        onClick={onClose}
        leftIcon={<CloseIcon w="12px" h="12px" />}
        variant="outline"
      >
        Cancelar
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Transferir Atendimento"
      modalFooter={footer}
      size="2xl"
    >
      <DynamicTable
        columns={columns}
        rows={users}
        isLoading={isLoading}
        size="md"
        containerStyle={{ boxShadow: "0 0 1em rgba(0,0,0, 0.05)" }}
      />
    </CustomModal>
  );
}

export const openModalUserTransferAttendance = (data: AttendanceId) =>
  makeEvent("openModalUserTransferAttendance", data);
